'use client';

import { useEffect, useRef, useState, useTransition } from 'react';

import { CheckCircle, Loader2, Sparkle, XCircle } from 'lucide-react';
import { flushSync } from 'react-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createAnonymousMonitors } from '@/lib/services/monitors';

type LogItem = { message: string; status: 'pending' | 'done' | 'error' };

export function QuickCreateForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [isPending, startTransition] = useTransition();
  const [log, setLog] = useState<LogItem[]>([]);
  const [visibleCount, setVisibleCount] = useState(0);

  // Manually manage auto-focusing input, instead of relying on
  // autoFocus prop, to avoid scrolling full page to focused input.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, []);

  function resetLog() {
    setLog([]);
    setVisibleCount(0);
  }

  function appendLog(message: string) {
    setLog((prev) => [...prev, { message, status: 'pending' }]);
    setVisibleCount((count) => count + 1);
  }

  function markLastDone(error: boolean = false) {
    setLog((prev) => {
      const next = [...prev];
      const last = next.findLast((item) => item.status === 'pending');
      if (last) last.status = error ? 'error' : 'done';
      return next;
    });
  }

  function sleep(delay: number) {
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  async function handleSubmit(formData: FormData) {
    const url = formData.get('url') as string;

    startTransition(async () => {
      try {
        // Force full re-render and flush.
        flushSync(() => {
          setLog([{ message: 'Auto-detecting relevant URLs…', status: 'pending' }]);
        });

        await sleep(1000);

        const formData = new FormData();
        formData.append('url', url);
        const result = await createAnonymousMonitors({}, formData);

        if (result.error) {
          markLastDone(true);
          appendLog(result.error);
          markLastDone(true);
          return;
        }

        await sleep(1500);
        markLastDone();

        appendLog('Creating monitors…');
        await sleep(1500);
        markLastDone();

        appendLog('Creating status page…');
        await sleep(1500);
        markLastDone();

        appendLog('Status page created. Redirecting now…');
        markLastDone();

        window.location.href = `/status/${result.slug}`; // Force full re-render to avoid themeing issues.
      } catch (err) {
        console.error('Attempt to create monitors and status page failed.');
        toast.error('Attempt to create monitors and status page failed.');
      }
    });
  }

  return (
    <div className="mx-auto w-full max-w-xl lg:max-w-full">
      <form
        ref={formRef}
        action={handleSubmit}
        className="flex flex-col items-stretch gap-3 sm:flex-row"
      >
        <Input
          ref={inputRef}
          name="url"
          placeholder="Enter your website or API URL"
          className="caret-healthy p-7 text-base focus-visible:ring-0 focus-visible:outline-none"
          disabled={isPending}
        />
        <Button
          type="submit"
          disabled={isPending}
          className="bg-healthy hover:bg-healthy/60 px-14 py-7"
        >
          {isPending ? (
            'Working…'
          ) : (
            <>
              <Sparkle /> Try It Out
            </>
          )}
        </Button>
      </form>

      {log.length > 0 && (
        <div className="border-secondary mt-6 w-full space-y-1 rounded-md border bg-black p-4 font-mono text-sm shadow-md backdrop-blur">
          {log.map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-2 opacity-100 transition-opacity duration-300"
              style={{ animationDelay: `${i * 150}ms`, animationFillMode: 'forwards' }}
            >
              <span className="mt-0.5 h-4 w-4">
                {item.status === 'done' ? (
                  <CheckCircle className="h-4 w-4 text-green-400" />
                ) : item.status === 'error' ? (
                  <XCircle className="h-4 w-4 text-red-400" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-yellow-400" />
                )}
              </span>
              <span>{item.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
